import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, tokens, tokenHeaders } from "@/lib/api";
import { startPayment } from "@/lib/pay";
import { Stepper, STEPS, StepJob, StepPersonal, StepEducation } from "@/components/apply/StepsA";
import { StepDocuments, StepReview, StepFee, PayButton, docRequired } from "@/components/apply/StepsB";
import { RecruitmentDisclaimer } from "@/components/site/Notices";

const EMPTY_P = { full_name: "", father_name: "", mother_name: "", dob: "", gender: "", mobile: "", email: "", address: "", state: "", district: "", pincode: "" };
const EMPTY_E = { highest_qualification: "", institution: "", passing_year: "", additional_qualifications: "", work_experience: "", skills: "" };

export default function Apply() {
  const { appId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ jobs: [], qualifications: [] });
  const [cfg, setCfg] = useState({ sms: false, payments: false });
  const [jobId, setJobId] = useState(params.get("job") || "");
  const [p, setP] = useState(EMPTY_P);
  const [e, setE] = useState(EMPTY_E);
  const [app, setApp] = useState(null);
  const [emailToken, setEmailToken] = useState(null);
  const [mobileToken, setMobileToken] = useState(null);
  const [consent, setConsent] = useState({ fee: false, terms: false });
  const [busy, setBusy] = useState(false);
  const job = data.jobs.find((j) => j.id === jobId);

  useEffect(() => {
    api.get("/jobs").then((r) => setData(r.data));
    api.get("/public-config").then((r) => setCfg(r.data));
  }, []);

  const reload = useCallback(async () => {
    const r = await api.get(`/applications/${appId}`, { headers: tokenHeaders("application", appId) });
    setApp(r.data);
    return r.data;
  }, [appId]);

  useEffect(() => {
    if (!appId) return;
    reload()
      .then((a) => {
        if (a.payment_status === "paid") return navigate(`/application/${a.id}`, { replace: true });
        setJobId(a.job_id);
        setP({ ...EMPTY_P, ...a.personal, gender: a.personal.gender || "" });
        if (a.education) setE({ ...EMPTY_E, ...a.education, passing_year: String(a.education.passing_year) });
        setStep(Number(params.get("step")) || (a.education ? (a.documents.length ? 5 : 4) : 3));
      })
      .catch(() => {
        toast.error("Application not found on this device. Use My Applications to open it.");
        navigate("/my-applications", { replace: true });
      });
  }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = appId ? tokenHeaders("application", appId) : {};
  const savePersonal = async () => {
    if (!app) {
      const r = await api.post("/applications", { job_id: jobId, personal: p, email_token: emailToken, mobile_token: mobileToken });
      tokens.set("application", r.data.id, r.data.access_token);
      navigate(`/apply/${r.data.id}?step=3`, { replace: true });
      return false;
    }
    await api.put(`/applications/${app.id}`, { personal: p, mobile_token: mobileToken }, { headers });
    await reload();
    return true;
  };

  const next = async () => {
    setBusy(true);
    try {
      if (step === 1 && !jobId) throw new Error("Please select a position");
      if (step === 2 && !(await savePersonal())) return;
      if (step === 3) {
        await api.put(`/applications/${app.id}`, { education: { ...e, passing_year: Number(e.passing_year) } }, { headers });
        await reload();
      }
      if (step === 4) {
        const missing = ["resume", "photo", "certificate"].filter((t) => docRequired(t, job) && !app.documents.some((d) => d.doc_type === t));
        if (missing.length) throw new Error(`Please upload: ${missing.join(", ")}`);
      }
      setStep(step + 1);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const res = await startPayment({ base: `/applications/${app.id}`, headers, payBody: { consent_fee: consent.fee, consent_terms: consent.terms }, description: `Application fee ${app.reference}` });
      if (res.status === "paid") return navigate(`/application/${app.id}`);
      toast.error(res.status === "dismissed" ? "Payment was not completed. You can retry." : res.error || "Payment failed. You can retry.");
      navigate(`/application/${app.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const canNext = step === 2 ? (app || emailToken) && (!cfg.sms || app?.mobile_verified || mobileToken) : true;

  return (
    <div className="container-x max-w-4xl py-12">
      <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Online application{app ? ` · ${app.reference}` : ""}</p>
      <h1 className="mb-8 mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="apply-step-title">{STEPS[step - 1]}</h1>
      <Stepper step={step} />
      {job && step > 1 && <p className="mb-6 rounded-xl bg-slate-900 px-4 py-3 text-sm text-white" data-testid="selected-job-summary">{job.title} · <span className="font-mono-num">{job.salary_text}</span> / month · {job.vacancies.toLocaleString("en-IN")} vacancies</p>}
      {step === 1 && <StepJob jobs={data.jobs} jobId={jobId} setJobId={setJobId} locked={!!app} />}
      {step === 1 && job && <p className="mt-4 rounded-xl bg-amber-50 p-4 text-sm" data-testid="selected-job-details">Selected: <strong>{job.title}</strong> — <span className="font-mono-num">{job.salary_text}</span> per month, {job.vacancies.toLocaleString("en-IN")} vacancies, {job.incentives}.</p>}
      {step === 2 && <StepPersonal p={p} setP={setP} emailLocked={!!app} emailVerified={!!app || !!emailToken} onEmailToken={setEmailToken} sms={cfg.sms} mobileVerified={(app?.mobile_verified && app.personal.mobile === p.mobile) || !!mobileToken} onMobileToken={setMobileToken} />}
      {step === 3 && <StepEducation e={e} setE={setE} qualifications={data.qualifications} job={job} />}
      {step === 4 && app && <StepDocuments app={app} job={job} reload={reload} />}
      {step === 5 && app && <StepReview app={app} goto={setStep} />}
      {step === 6 && app && <StepFee consent={consent} setConsent={setConsent} payments={cfg.payments} />}
      <div className="mt-8 flex items-center justify-between gap-3">
        <Button variant="outline" className="rounded-full" disabled={step === 1 || busy || (step === 3 && !!app && false)} onClick={() => setStep(step - 1)} data-testid="apply-back-button"><ArrowLeft className="h-4 w-4" /> Back</Button>
        {step < 6 ? (
          <Button className="rounded-full px-6" disabled={busy || !canNext} onClick={next} data-testid="apply-next-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {step === 5 ? "Proceed to fee" : "Continue"} <ArrowRight className="h-4 w-4" /></Button>
        ) : (
          <PayButton busy={busy} disabled={!consent.fee || !consent.terms || !cfg.payments} onClick={pay} />
        )}
      </div>
      <RecruitmentDisclaimer className="mt-10" />
    </div>
  );
}
